/**
 * Fabrica de chei pentru TanStack Query. UN singur loc, si nicio cheie scrisa cu mana altundeva.
 *
 * Motivul nu e estetica. O invalidare gresita intr-un instrument de trezorerie nu da o eroare: da
 * o cifra veche pe ecran, corect formatata, langa un total nou. Cu chei literale imprastiate prin
 * hooks, `['zi', data]` si `['zi', data, {}]` sunt doua cache-uri diferite care arata la fel, iar
 * cel invalidat dupa o scriere e mereu celalalt. Aici ierarhia e explicita si prefixul e mereu
 * invalidabil: `chei.zi.tot()` sterge orice varianta a zilei, `chei.zi.data(d)` doar una.
 *
 * Structura e `[radacina, domeniu, ...discriminanti]`:
 *   - `radacina` (`'api'`) separa datele de server de orice stare locala tinuta vreodata in cache;
 *   - `domeniu` corespunde 1:1 cu un fisier de rute din `functions/api/routes/`;
 *   - discriminantii sunt exact parametrii care schimba raspunsul, in aceeasi ordine ca in ruta.
 *
 * Cheile de MUTATIE traiesc tot aici pentru ca sunt SERIALIZATE: coada offline le scrie in
 * IndexedDB si, dupa reincarcare, le foloseste ca sa gaseasca `mutationFn`-ul potrivit prin
 * `setMutationDefaults`. Un string schimbat aici invalideaza o coada persistata pe disc.
 */

const RADACINA = 'api' as const;

/** Filtrele matricei, exact cele acceptate de `MatrixQuery`. */
export interface CheieMatrice {
  from: string;
  to: string;
  limit?: number;
  offset?: number;
  q?: string;
  onlyWithPayments?: boolean;
  sort?: 'name' | 'sold' | 'stoc' | 'payable';
}

export interface CheieCashflow {
  from: string;
  to: string;
  legalEntity?: 'EVO' | 'EDL' | 'TECHV';
}

export interface CheieComisioane {
  bankId?: number;
  onDate?: string;
  period?: string;
}

export interface CheieReconciliere {
  from?: string;
  to?: string;
  limit?: number;
}

export interface CheieActivitate {
  kind?: 'note' | 'invoice_ref' | 'supplier_rule' | 'tariff_doc';
  author?: string;
  limit?: number;
}

export const chei = {
  /** Prefixul absolut. `invalidateQueries({ queryKey: chei.tot() })` = „reincarca tot de la server". */
  tot: () => [RADACINA] as const,

  referinta: {
    tot: () => [RADACINA, 'referinta'] as const,
    fereastra: (fereastra?: { from?: string; to?: string }) =>
      [RADACINA, 'referinta', fereastra ?? null] as const,
  },

  /** `GET /api/eu` — verificarea de sesiune, separata de bootstrap ca sa poata fi ceruta singura. */
  eu: () => [RADACINA, 'eu'] as const,

  zi: {
    tot: () => [RADACINA, 'zi'] as const,
    data: (data: string) => [RADACINA, 'zi', data] as const,
  },

  matrice: {
    tot: () => [RADACINA, 'matrice'] as const,
    cu: (parametri: CheieMatrice) => [RADACINA, 'matrice', parametri] as const,
  },

  cashflow: {
    tot: () => [RADACINA, 'cashflow'] as const,
    interval: (interval: CheieCashflow) => [RADACINA, 'cashflow', interval] as const,
  },

  valutar: {
    tot: () => [RADACINA, 'valutar'] as const,
    data: (data: string) => [RADACINA, 'valutar', data] as const,
  },

  amanare: {
    tot: () => [RADACINA, 'amanare'] as const,
    /** Lantul complet al unei plati amanate, de la rădăcina la ultimul hop. */
    lant: (entryId: number) => [RADACINA, 'amanare', 'lant', entryId] as const,
    /** Ce ar face matura de seara pe o zi anume. */
    previzualizare: (data: string) => [RADACINA, 'amanare', 'previzualizare', data] as const,
  },

  comentarii: {
    tot: () => [RADACINA, 'comentarii'] as const,
    /**
     * Contoarele batched. `entityIds` se sorteaza in hook inainte de a ajunge aici: aceleasi id-uri
     * in alta ordine sunt acelasi raspuns, deci trebuie sa fie aceeasi cheie.
     */
    lot: (entityType: string, entityIds: readonly number[], kind?: string) =>
      [RADACINA, 'comentarii', 'lot', entityType, entityIds, kind ?? null] as const,
    /** Firul complet al unei singure entitati (panoul din drawer). */
    fir: (entityType: string, entityId: number, kind?: string) =>
      [RADACINA, 'comentarii', 'fir', entityType, entityId, kind ?? null] as const,
    cautare: (q: string, optiuni?: { limit?: number; entityType?: string }) =>
      [RADACINA, 'comentarii', 'cautare', q, optiuni ?? null] as const,
  },

  activitate: {
    tot: () => [RADACINA, 'activitate'] as const,
    cu: (parametri?: CheieActivitate) => [RADACINA, 'activitate', parametri ?? null] as const,
  },

  atasamente: {
    tot: () => [RADACINA, 'atasamente'] as const,
    lot: (entityType: string, entityIds: readonly number[]) =>
      [RADACINA, 'atasamente', 'lot', entityType, entityIds] as const,
  },

  comisioane: {
    tot: () => [RADACINA, 'comisioane'] as const,
    cu: (parametri?: CheieComisioane) => [RADACINA, 'comisioane', parametri ?? null] as const,
  },

  jurnal: {
    tot: () => [RADACINA, 'jurnal'] as const,
    lista: (filtre?: Readonly<Record<string, unknown>>) =>
      [RADACINA, 'jurnal', 'lista', filtre ?? null] as const,
  },

  furnizori: {
    tot: () => [RADACINA, 'furnizori'] as const,
    lista: (filtre?: Readonly<Record<string, unknown>>) =>
      [RADACINA, 'furnizori', 'lista', filtre ?? null] as const,
    unul: (id: number) => [RADACINA, 'furnizori', 'unul', id] as const,
  },

  reconciliere: {
    tot: () => [RADACINA, 'reconciliere'] as const,
    cu: (parametri?: CheieReconciliere) => [RADACINA, 'reconciliere', parametri ?? null] as const,
  },

  utilizatori: {
    tot: () => [RADACINA, 'utilizatori'] as const,
    lista: () => [RADACINA, 'utilizatori', 'lista'] as const,
  },

  raport: {
    tot: () => [RADACINA, 'raport'] as const,
    perioada: (perioada: string) => [RADACINA, 'raport', perioada] as const,
  },

  importuri: {
    tot: () => [RADACINA, 'importuri'] as const,
    cu: (parametri?: { status?: string; limit?: number }) =>
      [RADACINA, 'importuri', parametri ?? null] as const,
  },

  credentiale: {
    /**
     * DOAR lista de metadate. Valoarea dezvaluita a unui secret nu are cheie de cache si nici nu
     * va avea: se intoarce dintr-o mutatie, o singura data, si moare cu componenta care a cerut-o.
     */
    tot: () => [RADACINA, 'credentiale'] as const,
    lista: () => [RADACINA, 'credentiale', 'lista'] as const,
  },

  /**
   * Cheile de mutatie. Se serializeaza in coada offline, deci sunt un contract pe disc.
   *
   * Nivelul cu id (`patchIntrare(42)`) exista ca sa se poata filtra starea de salvare PER CELULA cu
   * `useMutationState`; prefixul fara id (`patchIntrare()`) e cel pe care se inregistreaza
   * `setMutationDefaults`, ca o mutatie restaurata dupa reload sa-si regaseasca functia.
   */
  mutatii: {
    tot: () => [RADACINA, 'mutatie'] as const,

    creeazaRunda: () => [RADACINA, 'mutatie', 'zi', 'runda'] as const,
    adaugaPlata: () => [RADACINA, 'mutatie', 'zi', 'plata-noua'] as const,
    patchFurnizor: () => [RADACINA, 'mutatie', 'furnizor', 'patch'] as const,
    stergePlata: (entryId?: number) =>
      (entryId === undefined
        ? [RADACINA, 'mutatie', 'zi', 'plata-stearsa']
        : [RADACINA, 'mutatie', 'zi', 'plata-stearsa', entryId]) as readonly unknown[],
    restaureazaPlata: (entryId?: number) =>
      (entryId === undefined
        ? [RADACINA, 'mutatie', 'zi', 'plata-restaurata']
        : [RADACINA, 'mutatie', 'zi', 'plata-restaurata', entryId]) as readonly unknown[],
    patchIntrare: (entryId?: number) =>
      (entryId === undefined
        ? [RADACINA, 'mutatie', 'intrare', 'patch']
        : [RADACINA, 'mutatie', 'intrare', 'patch', entryId]) as readonly unknown[],
    actiuniBloc: () => [RADACINA, 'mutatie', 'intrare', 'bloc'] as const,
    amanare: (entryId?: number) =>
      (entryId === undefined
        ? [RADACINA, 'mutatie', 'amanare', 'plata']
        : [RADACINA, 'mutatie', 'amanare', 'plata', entryId]) as readonly unknown[],
    amanareAuto: () => [RADACINA, 'mutatie', 'amanare', 'auto'] as const,

    pozitieCash: (data?: string, accountId?: number) =>
      (data === undefined || accountId === undefined
        ? [RADACINA, 'mutatie', 'cashflow', 'pozitie']
        : [RADACINA, 'mutatie', 'cashflow', 'pozitie', data, accountId]) as readonly unknown[],

    patchCotatie: (quoteId?: number) =>
      (quoteId === undefined
        ? [RADACINA, 'mutatie', 'fx', 'cotatie']
        : [RADACINA, 'mutatie', 'fx', 'cotatie', quoteId]) as readonly unknown[],
    cotatiiBulk: () => [RADACINA, 'mutatie', 'fx', 'bulk'] as const,
    preiaBnr: (quoteId?: number) =>
      (quoteId === undefined
        ? [RADACINA, 'mutatie', 'fx', 'bnr']
        : [RADACINA, 'mutatie', 'fx', 'bnr', quoteId]) as readonly unknown[],
    setCastigator: (txnId?: number) =>
      (txnId === undefined
        ? [RADACINA, 'mutatie', 'fx', 'castigator']
        : [RADACINA, 'mutatie', 'fx', 'castigator', txnId]) as readonly unknown[],

    postComentariu: () => [RADACINA, 'mutatie', 'comentariu', 'creeaza'] as const,
    patchComentariu: (id?: number) =>
      (id === undefined
        ? [RADACINA, 'mutatie', 'comentariu', 'patch']
        : [RADACINA, 'mutatie', 'comentariu', 'patch', id]) as readonly unknown[],
    rezolvaComentariu: (id?: number) =>
      (id === undefined
        ? [RADACINA, 'mutatie', 'comentariu', 'rezolva']
        : [RADACINA, 'mutatie', 'comentariu', 'rezolva', id]) as readonly unknown[],

    incarcaAtasament: () => [RADACINA, 'mutatie', 'atasament', 'incarca'] as const,
    stergeAtasament: (id?: number) =>
      (id === undefined
        ? [RADACINA, 'mutatie', 'atasament', 'sterge']
        : [RADACINA, 'mutatie', 'atasament', 'sterge', id]) as readonly unknown[],
    marcheazaScanare: () => [RADACINA, 'mutatie', 'atasament', 'scan'] as const,

    /**
     * Autentificarea NU se pune niciodata in coada offline.
     *
     * O parola scrisa in IndexedDB si retrimisa automat peste doua ore, cand revine reteaua, ar
     * fi si o scurgere, si un login pe care nu l-a cerut nimeni in momentul acela.
     */
    login: () => [RADACINA, 'mutatie', 'auth', 'login'] as const,
    logout: () => [RADACINA, 'mutatie', 'auth', 'logout'] as const,
    stepUp: () => [RADACINA, 'mutatie', 'auth', 'stepup'] as const,
    schimbaParola: () => [RADACINA, 'mutatie', 'auth', 'parola'] as const,
    creeazaUtilizator: () => [RADACINA, 'mutatie', 'utilizator', 'creeaza'] as const,
    patchUtilizator: () => [RADACINA, 'mutatie', 'utilizator', 'patch'] as const,
    reseteazaParola: () => [RADACINA, 'mutatie', 'utilizator', 'parola'] as const,

    incarcaImport: () => [RADACINA, 'mutatie', 'import', 'incarca'] as const,
    rezolvaConflict: () => [RADACINA, 'mutatie', 'import', 'conflict'] as const,
    aplicaImport: () => [RADACINA, 'mutatie', 'import', 'aplica'] as const,

    supersedeTarif: (bankId?: number) =>
      (bankId === undefined
        ? [RADACINA, 'mutatie', 'tarif', 'supersede']
        : [RADACINA, 'mutatie', 'tarif', 'supersede', bankId]) as readonly unknown[],

    /** Dezvaluirea unui secret e o mutatie tocmai ca sa NU aiba cache. Nu se persista niciodata. */
    revealCredential: () => [RADACINA, 'mutatie', 'credential', 'reveal'] as const,
    seteazaSecret: () => [RADACINA, 'mutatie', 'credential', 'secret'] as const,
    actualizeazaCredentiala: () => [RADACINA, 'mutatie', 'credential', 'meta'] as const,
  },
} as const;

/**
 * `true` daca cheia data ESTE cheia lui `/api/eu`.
 *
 * Exista pentru interceptorul global de 401 din `main.tsx`: el invalideaza `eu` la orice sesiune
 * expirata, dar trebuie sa se opreasca inainte de a se aplica LUI `eu` — altfel interogarea care
 * constata lipsa sesiunii s-ar reinteroga singura la infinit.
 *
 * Comparatie element cu element, nu `JSON.stringify`: cheile pot contine obiecte a caror ordine de
 * campuri nu e garantata, iar aici oricum ne intereseaza doar un prefix scurt si literal.
 */
export function esteCheiaEu(cheie: readonly unknown[]): boolean {
  const referinta = chei.eu();
  return cheie.length === referinta.length && cheie.every((parte, i) => parte === referinta[i]);
}

/**
 * Id-urile se sorteaza si se deduplica INAINTE de a intra in cheie.
 *
 * Grila cere contoare pentru aceleasi 30 de entitati la fiecare redesenare, dar in ordinea in care
 * se intampla sa fie sortate randurile. Fara normalizare, fiecare sortare a grilei ar produce un
 * cache nou si o cerere noua pentru exact acelasi raspuns.
 */
export function normalizeazaIds(ids: readonly number[]): readonly number[] {
  return [...new Set(ids)].sort((a, b) => a - b);
}
